import type { RouteLocationNormalized } from "vue-router";

import type { HttpResponse } from "@/backend/http/api.config.axios";
import type { LoginResponse } from "@/backend/http/auth";
import Auth from "@/backend/http/auth";
import { loggedIn } from "@/backend/read/BackendReader";
import { adminUrl, isCloud } from "@/utils/env/Environment";
import { firstQueryParam } from "@/utils/vue/queryParam";

import { companyParam, removeCompanyParam } from "./utils";

export async function beforeLogin(to: RouteLocationNormalized) {
  // going back from the app
  if (loggedIn()) {
    return leaveLogin(to, "/");
  }

  // directly accessing the login page
  try {
    if (await Auth.isAuthenticated()) {
      if (companyParam()) {
        // already a valid cookie -> ignore and delete the company param to avoid endless loop
        // we could check if cookie company is different from param company and log out.
        // but does this really happen?
        removeCompanyParam();
      }
      return leaveLogin(to, firstQueryParam(to.query, "path") || "/");
    }
  } catch {
    // not authenticated — fall through to login page
  }

  if (isCloud) {
    const companyId = companyParam();
    if (!companyId) {
      return;
    }
    // go to FusionAuth login page with company already resolved
    const res = await Auth.login({
      type: firstQueryParam(to.query, "type") || "",
      path: firstQueryParam(to.query, "path") || "",
      companyId,
    });
    return followRedirect(res);
  }

  // go to FusionAuth login page
  const res = await Auth.loginPage({
    type: firstQueryParam(to.query, "type") || "",
    path: firstQueryParam(to.query, "path") || "",
  });
  return followRedirect(res);

  function followRedirect(res: HttpResponse<LoginResponse>) {
    if (res.data.success && res.data.redirectUrl) {
      window.location.assign(res.data.redirectUrl);
      return false;
    }
  }
}

/**
 * Where a user who is already signed in goes instead of the login page.
 * authserver has no login page of its own, so pi-admin's login lands here with type=admin:
 * those users are sent to pi-admin at the requested path, and `false` cancels the navigation
 * while the page unloads. Everyone else is routed to `appPath` in this app.
 */
function leaveLogin(to: RouteLocationNormalized, appPath: string) {
  if (firstQueryParam(to.query, "type") === "admin") {
    const path = firstQueryParam(to.query, "path") || "";
    window.location.replace(`${adminUrl}/${path.replace(/^\//, "")}`);
    return false;
  }
  return { path: appPath, replace: true };
}
